import {
  CompChangeKind,
  CompPeriodSource,
  DEFAULT_HOURS_PER_WEEK,
  PayrollCompPeriod,
  PayrollPosition,
  PayrollScheduledRaise,
  WEEKS_PER_YEAR,
} from '../types/finances';

function startOfMonth(d: Date): Date {
  const r = new Date(d);
  r.setHours(0, 0, 0, 0);
  r.setDate(1);
  return r;
}

function endOfMonth(d: Date): Date {
  const r = startOfMonth(d);
  r.setMonth(r.getMonth() + 1);
  r.setMilliseconds(-1);
  return r;
}

function startOfDay(d: Date): Date {
  const r = new Date(d);
  r.setHours(0, 0, 0, 0);
  return r;
}

function dayBefore(d: Date): Date {
  const r = startOfDay(d);
  r.setDate(r.getDate() - 1);
  return r;
}

const MONTH_LABELS = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

export function monthLabel(d: Date): string {
  return `${MONTH_LABELS[d.getMonth()]} ${d.getFullYear()}`;
}

export function applyCompChange(base: number, kind: CompChangeKind, amount: number): number {
  if (!Number.isFinite(base) || base < 0) return 0;
  if (kind === 'percent') return base * (1 + amount / 100);
  if (kind === 'dollar') return base + amount;
  if (kind === 'set') return Math.max(0, amount);
  return base;
}

export function deriveAnnualFromHourly(hourlyRate: number, hoursPerWeek: number): number {
  return hourlyRate * hoursPerWeek * WEEKS_PER_YEAR;
}

export function deriveHourlyFromAnnual(annualSalary: number, hoursPerWeek: number): number {
  const hoursPerYear = hoursPerWeek * WEEKS_PER_YEAR;
  if (hoursPerYear <= 0) return 0;
  return annualSalary / hoursPerYear;
}

export function applyRaiseToComp(
  position: Pick<PayrollPosition, 'payType' | 'annualSalary' | 'hourlyRate' | 'hoursPerWeek'>,
  kind: CompChangeKind,
  amount: number,
): { annualSalary: number; hourlyRate: number | null; hoursPerWeek: number | null } {
  const hoursPerWeek = position.hoursPerWeek ?? DEFAULT_HOURS_PER_WEEK;

  if (position.payType === 'hourly') {
    const currentHourly = position.hourlyRate ?? deriveHourlyFromAnnual(position.annualSalary, hoursPerWeek);
    let nextHourly = currentHourly;
    if (kind === 'set') {
      const annual = Math.max(0, amount);
      return {
        annualSalary: annual,
        hourlyRate: deriveHourlyFromAnnual(annual, hoursPerWeek),
        hoursPerWeek,
      };
    }
    if (kind === 'percent') nextHourly = applyCompChange(currentHourly, kind, amount);
    else nextHourly = applyCompChange(currentHourly, 'dollar', amount / (hoursPerWeek * WEEKS_PER_YEAR));
    return {
      annualSalary: deriveAnnualFromHourly(nextHourly, hoursPerWeek),
      hourlyRate: nextHourly,
      hoursPerWeek,
    };
  }

  const nextAnnual = applyCompChange(position.annualSalary || 0, kind, amount);
  return { annualSalary: nextAnnual, hourlyRate: null, hoursPerWeek: null };
}

function compPeriodCoversMonth(period: PayrollCompPeriod, month: Date): boolean {
  const monthStart = startOfMonth(month);
  const monthEnd = endOfMonth(month);
  const effective = startOfDay(new Date(period.effectiveDate));
  if (effective > monthEnd) return false;
  if (period.endDate) {
    const end = startOfDay(new Date(period.endDate));
    if (end < monthStart) return false;
  }
  return true;
}

function sortedCompPeriods(position: PayrollPosition): PayrollCompPeriod[] {
  return [...(position.compPeriods ?? [])].sort(
    (a, b) => new Date(a.effectiveDate).getTime() - new Date(b.effectiveDate).getTime(),
  );
}

function sortedScheduledRaises(position: PayrollPosition): PayrollScheduledRaise[] {
  return [...(position.scheduledRaises ?? [])].sort(
    (a, b) => new Date(a.effectiveDate).getTime() - new Date(b.effectiveDate).getTime(),
  );
}

/** Salary for a forecast month — uses comp periods + not-yet-applied scheduled raises. */
export function resolveAnnualSalaryForMonth(position: PayrollPosition, month: Date): number {
  const resolved = resolvePositionForMonth(position, month);
  return resolved.annualSalary || 0;
}

/** Closed comp period covering a month (open-ended periods are history labels only). */
function findClosedCompPeriodForMonth(
  periods: PayrollCompPeriod[],
  month: Date,
): PayrollCompPeriod | null {
  const covering = periods.filter(p => p.endDate != null && compPeriodCoversMonth(p, month));
  if (covering.length === 0) return null;
  return covering[covering.length - 1];
}

/** Position copy with comp resolved for the given calendar month. */
export function resolvePositionForMonth(position: PayrollPosition, month: Date): PayrollPosition {
  const closed = findClosedCompPeriodForMonth(sortedCompPeriods(position), month);
  if (closed) {
    return {
      ...position,
      annualSalary: closed.annualSalary,
      hourlyRate: closed.hourlyRate ?? position.hourlyRate ?? null,
      hoursPerWeek: closed.hoursPerWeek ?? position.hoursPerWeek ?? null,
    };
  }

  let annualSalary = position.annualSalary || 0;
  let hourlyRate = position.hourlyRate ?? null;
  let hoursPerWeek = position.hoursPerWeek ?? null;
  const monthEnd = endOfMonth(month);

  for (const raise of sortedScheduledRaises(position)) {
    const effective = startOfDay(new Date(raise.effectiveDate));
    if (effective > monthEnd) break;
    const next = applyRaiseToComp(
      { payType: position.payType, annualSalary, hourlyRate, hoursPerWeek },
      raise.kind,
      raise.amount,
    );
    annualSalary = next.annualSalary;
    hourlyRate = next.hourlyRate;
    hoursPerWeek = next.hoursPerWeek;
  }

  return { ...position, annualSalary, hourlyRate, hoursPerWeek };
}

function inferCompPeriodSource(raise: PayrollScheduledRaise): CompPeriodSource {
  const label = (raise.label || '').toLowerCase();
  if (label.includes('cola')) return 'cola';
  return 'raise';
}

function openCompPeriod(periods: PayrollCompPeriod[]): PayrollCompPeriod | null {
  const open = periods.filter(p => !p.endDate);
  if (open.length === 0) return null;
  return open.sort(
    (a, b) => new Date(b.effectiveDate).getTime() - new Date(a.effectiveDate).getTime(),
  )[0];
}

/** Seed a single open comp period for legacy positions missing history. */
export function seedLegacyCompPeriod(position: PayrollPosition): PayrollPosition {
  if (position.compPeriods && position.compPeriods.length > 0) return position;
  const period: PayrollCompPeriod = {
    id: `period-${position.id}-initial`,
    effectiveDate: new Date(position.startDate),
    endDate: null,
    annualSalary: position.annualSalary || 0,
    hourlyRate: position.hourlyRate ?? null,
    hoursPerWeek: position.hoursPerWeek ?? null,
    label: 'Initial',
    source: 'hire',
  };
  return { ...position, compPeriods: [period] };
}

/** Apply scheduled raises whose effective date has arrived. Returns null if nothing due. */
export function applyDueScheduledRaises(
  position: PayrollPosition,
  asOfDate: Date = new Date(),
): PayrollPosition | null {
  const asOf = startOfDay(asOfDate);
  const due = sortedScheduledRaises(position).filter(
    r => startOfDay(new Date(r.effectiveDate)) <= asOf,
  );
  if (due.length === 0) return null;

  let next: PayrollPosition = seedLegacyCompPeriod({ ...position });
  let periods = [...(next.compPeriods ?? [])];

  for (const raise of due) {
    const effective = startOfDay(new Date(raise.effectiveDate));
    const currentOpen = openCompPeriod(periods);
    if (currentOpen) {
      periods = periods.map(p =>
        p.id === currentOpen.id ? { ...p, endDate: dayBefore(effective) } : p,
      );
    }

    const comp = applyRaiseToComp(next, raise.kind, raise.amount);
    const newPeriod: PayrollCompPeriod = {
      id: `period-${raise.id}`,
      effectiveDate: effective,
      endDate: null,
      annualSalary: comp.annualSalary,
      hourlyRate: comp.hourlyRate,
      hoursPerWeek: comp.hoursPerWeek,
      label: raise.label,
      source: inferCompPeriodSource(raise),
    };
    periods = [...periods, newPeriod];

    next = {
      ...next,
      annualSalary: comp.annualSalary,
      hourlyRate: comp.hourlyRate,
      hoursPerWeek: comp.hoursPerWeek,
      compPeriods: periods,
    };
  }

  const dueIds = new Set(due.map(r => r.id));
  next.scheduledRaises = (next.scheduledRaises ?? []).filter(r => !dueIds.has(r.id));
  return next;
}

export interface PositionCompTimelinePoint {
  month: Date;
  label: string;
  annualSalary: number;
  eventLabel?: string;
}

export interface PositionCompTimelineOptions {
  monthsBack?: number;
  monthsForward?: number;
  fromMonth?: Date;
}

/** Month series of resolved annual salary (loaded cost computed by caller). */
export function buildPositionSalaryTimeline(
  position: PayrollPosition,
  options: PositionCompTimelineOptions = {},
): PositionCompTimelinePoint[] {
  const from = startOfMonth(options.fromMonth ?? new Date());
  const monthsBack = options.monthsBack ?? 12;
  const monthsForward = options.monthsForward ?? 12;
  const total = monthsBack + monthsForward + 1;
  const start = new Date(from);
  start.setMonth(start.getMonth() - monthsBack);

  const raiseMonths = new Map<string, string>();
  for (const r of position.scheduledRaises ?? []) {
    const key = monthLabel(startOfMonth(new Date(r.effectiveDate)));
    raiseMonths.set(key, r.label || 'Scheduled raise');
  }
  for (const p of position.compPeriods ?? []) {
    const key = monthLabel(startOfMonth(new Date(p.effectiveDate)));
    raiseMonths.set(key, p.label || 'Comp change');
  }

  const out: PositionCompTimelinePoint[] = [];
  for (let i = 0; i < total; i++) {
    const month = new Date(start);
    month.setMonth(start.getMonth() + i);
    const resolved = resolvePositionForMonth(position, month);
    const label = monthLabel(month);
    out.push({
      month,
      label,
      annualSalary: resolved.annualSalary || 0,
      eventLabel: raiseMonths.get(label),
    });
  }
  return out;
}

export function nextScheduledRaise(position: PayrollPosition): PayrollScheduledRaise | null {
  const today = startOfDay(new Date());
  const upcoming = sortedScheduledRaises(position).filter(
    r => startOfDay(new Date(r.effectiveDate)) > today,
  );
  return upcoming[0] ?? null;
}

export function projectRaiseComp(
  position: PayrollPosition,
  raise: Pick<PayrollScheduledRaise, 'kind' | 'amount'>,
): { annualSalary: number; hourlyRate: number | null } {
  const next = applyRaiseToComp(position, raise.kind, raise.amount);
  return { annualSalary: next.annualSalary, hourlyRate: next.hourlyRate };
}

export function newScheduledRaiseId(): string {
  return `raise-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

export function newCompPeriodId(): string {
  return `period-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}
